import { useMemo } from "react";
import { ComplianceCaseTypeEnum as CaseType } from "~/graphql/supergraphEnums";
import {
  useGetCaseWithRelationshipsQuery,
  useGetOnboardingCasesForPositionsQuery,
  useGetPositionsForCaseQuery,
} from "~/graphql/supergraphHooks";
import { type ComplianceCaseTypeEnum } from "~/graphql/supergraphTypes";
import { useReusableProfilesFeatureFlag } from "~/pages/Compliance/flags";
import { toGlobalId } from "~/utils/graphql-helpers";
import {
  rowsFromPositionsData,
  rowsFromQuery,
} from "../IssueGrid/ImpactedGPGrid/impactedGPGraphqlDataSource";
import {
  type EscalationPreviewRow,
  externalPreviewRows,
  type ParentRelationship,
} from "./escalationPreview";

type Args = {
  caseId: number;
  caseType: ComplianceCaseTypeEnum;
  escalatedProfileId: number | undefined;
  relationships: readonly ParentRelationship[] | null | undefined;
  skip?: boolean;
};

/** The client/position rows an external escalation fans out to, as the impacted-GP grid computes them. */
export const useExternalEscalationPreview = ({
  caseId,
  caseType,
  escalatedProfileId,
  relationships,
  skip,
}: Args) => {
  const useReusableProfiles = useReusableProfilesFeatureFlag();
  const enabled = !skip && escalatedProfileId != null;

  const caseQuery = useGetCaseWithRelationshipsQuery({
    variables: { caseId },
    skip: !enabled,
    fetchPolicy: "no-cache",
  });
  const positionsQuery = useGetPositionsForCaseQuery({
    variables: {
      profileId: escalatedProfileId ?? 0,
      caseTypes: [CaseType.InvestorOnboarding, CaseType.Remediation],
    },
    skip: !enabled,
    fetchPolicy: "no-cache",
  });

  const complianceProfile = positionsQuery.data?.complianceProfile;
  const positionIds = useMemo(
    () =>
      (complianceProfile?.positions ?? [])
        .map((position) => position.id)
        .filter((id): id is string => id != null),
    [complianceProfile?.positions],
  );
  const arenaIds = useMemo(
    () =>
      (complianceProfile?.clients ?? []).map((clientId: number) =>
        toGlobalId("ArenaObjectType", clientId),
      ),
    [complianceProfile?.clients],
  );

  const onboardingCasesQuery = useGetOnboardingCasesForPositionsQuery({
    variables: {
      positionFilters: { arenaIds, positionIds, includeFundAdminEntities: false },
    },
    skip: !enabled || !useReusableProfiles || positionIds.length === 0,
    fetchPolicy: "no-cache",
  });

  const rows = useMemo<EscalationPreviewRow[]>(() => {
    const complianceCase = caseQuery.data?.complianceCase;
    if (!enabled || !complianceCase || !complianceProfile) return [];

    const onboardingPositions = onboardingCasesQuery.data?.positions;
    const impactedRows = useReusableProfiles
      ? positionIds.length === 0 || !onboardingPositions
        ? []
        : rowsFromPositionsData(complianceCase, onboardingPositions, caseType)
      : rowsFromQuery(complianceCase, complianceProfile, caseType);

    return externalPreviewRows(impactedRows, relationships, caseId, escalatedProfileId);
  }, [
    enabled,
    caseQuery.data?.complianceCase,
    complianceProfile,
    useReusableProfiles,
    positionIds.length,
    onboardingCasesQuery.data?.positions,
    caseType,
    relationships,
    caseId,
    escalatedProfileId,
  ]);

  return {
    rows,
    loading: caseQuery.loading || positionsQuery.loading || onboardingCasesQuery.loading,
    error: caseQuery.error ?? positionsQuery.error ?? onboardingCasesQuery.error,
  };
};
